import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Play, Dice6, SkipForward, StopCircle, Settings, Save, History,
  Trash2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { DIFFICULTY_COLORS, DIFFICULTY_LABELS } from './combatConstants';

export function CombatToolbar({
  encounter,
  language,
  onRollInitiatives,
  onStartCombat,
  onNextTurn,
  onEndCombat,
  onUpdateSettings,
  onSaveAsTemplate,
  onDeleteEncounter,
  onToggleCombatLog,
  combatLogOpen
}) {
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', tags: '' });

  const handleSaveTemplate = () => {
    onSaveAsTemplate({
      name: templateForm.name || `Template: ${encounter.name}`,
      description: templateForm.description,
      tags: templateForm.tags.split(',').map((t) => t.trim()).filter(Boolean)
    });
    setSaveTemplateOpen(false);
    setTemplateForm({ name: '', description: '', tags: '' });
  };

  return (
    <div className="p-3 border-b border-border bg-card/50 flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-2">
        <h1 className="font-cinzel font-bold text-xl truncate max-w-[200px]">
          {encounter.name}
        </h1>
        <Badge variant="outline" className={cn('text-white', DIFFICULTY_COLORS[encounter.difficulty])}>
          {DIFFICULTY_LABELS[language]?.[encounter.difficulty]}
        </Badge>
        <Badge variant="secondary">{encounter.total_xp} XP</Badge>
        {encounter.status === 'active' && (
          <Badge className="bg-green-500">Round {encounter.current_round}</Badge>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {encounter.status === 'preparing' && (
          <>
            <Button variant="outline" size="sm" onClick={onRollInitiatives} data-testid="roll-initiative-btn">
              <Dice6 className="mr-1 h-4 w-4" />
              {language === 'fr' ? 'Init.' : 'Init.'}
            </Button>
            <Button size="sm" onClick={onStartCombat} disabled={!encounter.combatants?.length} data-testid="start-combat-btn">
              <Play className="mr-1 h-4 w-4" />
              {language === 'fr' ? 'Demarrer' : 'Start'}
            </Button>
          </>
        )}

        {encounter.status === 'active' && (
          <>
            <Button size="sm" onClick={onNextTurn} data-testid="next-turn-btn">
              <SkipForward className="mr-1 h-4 w-4" />
              {language === 'fr' ? 'Suivant' : 'Next'}
            </Button>
            <Button variant="destructive" size="sm" onClick={onEndCombat} data-testid="end-combat-btn">
              <StopCircle className="mr-1 h-4 w-4" />
              {language === 'fr' ? 'Fin' : 'End'}
            </Button>
          </>
        )}

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="settings-btn">
              <Settings className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72" align="end">
            <div className="space-y-4">
              <h4 className="font-semibold">{language === 'fr' ? 'Parametres du combat' : 'Combat settings'}</h4>

              <div className="space-y-2">
                <Label className="text-sm">{language === 'fr' ? 'Affichage des PV' : 'HP Display'}</Label>
                <Select
                  value={encounter.hp_display_mode || 'exact'}
                  onValueChange={(v) => onUpdateSettings({ hp_display_mode: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exact">{language === 'fr' ? 'Valeur exacte' : 'Exact value'}</SelectItem>
                    <SelectItem value="status">{language === 'fr' ? 'Etat (En forme/Blesse...)' : 'Status (Healthy/Injured...)'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm">{language === 'fr' ? 'Relance auto initiative' : 'Auto re-roll initiative'}</Label>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'A chaque nouveau round' : 'At each new round'}
                  </p>
                </div>
                <Switch
                  checked={encounter.auto_reroll_initiative || false}
                  onCheckedChange={(v) => onUpdateSettings({ auto_reroll_initiative: v })}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {encounter.combatants?.length > 0 && (
          <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="save-template-btn">
                <Save className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-cinzel">
                  {language === 'fr' ? 'Sauvegarder comme modele' : 'Save as template'}
                </DialogTitle>
                <DialogDescription>
                  {language === 'fr'
                    ? 'Creez un modele reutilisable a partir de cette rencontre'
                    : 'Create a reusable template from this encounter'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Nom du modele' : 'Template name'}</Label>
                  <Input
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder={`Template: ${encounter.name}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
                  <Textarea
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, description: e.target.value }))}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Tags (separes par virgule)' : 'Tags (comma separated)'}</Label>
                  <Input
                    value={templateForm.tags}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, tags: e.target.value }))}
                    placeholder="foret, embuscade, niveau 1-3"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSaveTemplateOpen(false)}>
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button onClick={handleSaveTemplate}>
                  <Save className="mr-2 h-4 w-4" />
                  {language === 'fr' ? 'Sauvegarder' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCombatLog}
          data-testid="combat-log-btn"
          aria-pressed={combatLogOpen}
        >
          <History className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onDeleteEncounter(encounter.id)} data-testid="delete-encounter-btn">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
}
